import type { ComparisonConfig } from './ComparisonPage';

/* ========================================================================== */
/*  HotelStack.io vs innRoad                                                  */
/* ========================================================================== */

export const HOTELSTACK_VS_INNROAD: ComparisonConfig = {
  slug: 'hotelstack-vs-innroad',
  competitorName: 'innRoad',
  heroTitle: 'HotelStack.io vs innRoad',
  heroSubtitle: 'innRoad is a US-based cloud PMS for independent hotels with a basic feature set. HotelStack.io delivers 36 modules including advanced revenue management, F&B/POS, multi-property support, and global compliance — at a comparable price point.',
  overviewComparison: [
    { label: 'Total Modules', hotelstack: '36 Integrated Modules', competitor: '8+ Core Features' },
    { label: 'Pricing', hotelstack: 'From $49/mo', competitor: 'From $100+/mo' },
    { label: 'Multi-Property', hotelstack: 'Full Chain Management', competitor: 'Not Supported' },
    { label: 'F&B / POS', hotelstack: 'Built-In', competitor: 'Not Available' },
    { label: 'Global Reach', hotelstack: '40+ Countries', competitor: 'US-Focused' },
    { label: 'Revenue Management', hotelstack: 'Advanced (Built-In)', competitor: 'Basic Rate Tools' },
  ],
  featureTable: [
    {
      category: 'Core PMS',
      features: [
        { name: 'Cloud-Based PMS', hotelstack: true, competitor: true },
        { name: 'Visual Room Board', hotelstack: true, competitor: true },
        { name: 'Reservation Management', hotelstack: true, competitor: true },
        { name: 'Night Audit Wizard', hotelstack: true, competitor: true },
        { name: 'Guest Folio Management', hotelstack: true, competitor: true },
        { name: 'Online Check-In Portal', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Distribution & Booking',
      features: [
        { name: 'Channel Manager (100+ OTAs)', hotelstack: true, competitor: 'Limited OTAs' },
        { name: 'GDS Connectivity', hotelstack: true, competitor: true },
        { name: 'Commission-Free Booking Engine', hotelstack: true, competitor: true },
        { name: 'Group Bookings', hotelstack: true, competitor: 'Basic' },
        { name: 'Corporate Rate Management', hotelstack: true, competitor: 'Limited' },
      ],
    },
    {
      category: 'Revenue Management',
      features: [
        { name: 'Dynamic Pricing Rules', hotelstack: true, competitor: 'Basic' },
        { name: 'What-If Revenue Simulator', hotelstack: true, competitor: false },
        { name: 'Competitor Rate Monitor', hotelstack: true, competitor: false },
        { name: 'Demand Forecasting', hotelstack: true, competitor: false },
        { name: 'RevPAR Analytics', hotelstack: true, competitor: 'Basic' },
      ],
    },
    {
      category: 'Operations & F&B',
      features: [
        { name: 'POS Terminal', hotelstack: true, competitor: false },
        { name: 'Room Service Module', hotelstack: true, competitor: false },
        { name: 'Housekeeping Management', hotelstack: true, competitor: 'Basic' },
        { name: 'Maintenance Work Orders', hotelstack: true, competitor: false },
        { name: 'Staff Scheduling', hotelstack: true, competitor: false },
        { name: 'Kitchen Display System', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Reporting & Compliance',
      features: [
        { name: 'Flash Report', hotelstack: true, competitor: true },
        { name: 'P&L Statement', hotelstack: true, competitor: false },
        { name: 'Pace Report', hotelstack: true, competitor: false },
        { name: 'Multi-Currency Billing', hotelstack: true, competitor: false },
        { name: 'Regional Tax Compliance', hotelstack: true, competitor: 'US Only' },
        { name: 'Custom Report Builder', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Resilience & Offline',
      features: [
        { name: 'Offline-Capable PWA', hotelstack: true, competitor: false },
        { name: 'Offline Mutation Queue', hotelstack: true, competitor: false },
        { name: 'Background Sync on Reconnect', hotelstack: true, competitor: false },
        { name: 'Conflict-Aware Reconciliation', hotelstack: true, competitor: false },
        { name: 'Installable Front Desk', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Corporate & Group Sales',
      features: [
        { name: 'Corporate Rate Management', hotelstack: true, competitor: 'Limited' },
        { name: 'Group Booking with Rooming Lists', hotelstack: true, competitor: 'Basic' },
        { name: 'AR Aging & Production Reports', hotelstack: true, competitor: false },
        { name: 'Dining Reservations', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Staff & HR',
      features: [
        { name: 'Staff Directory & Scheduling', hotelstack: true, competitor: false },
        { name: 'Payroll Processing', hotelstack: true, competitor: false },
        { name: 'Leave & Attendance', hotelstack: true, competitor: false },
        { name: 'Performance Analytics', hotelstack: true, competitor: false },
      ],
    },
  ],
  pricingComparison: {
    hotelstackStarting: '$49',
    competitorStarting: '$100+',
    hotelstackNote: 'All 36 modules included. Global multi-currency support. Regional pricing in EUR, GBP, AED, INR, THB. Free onboarding and migration.',
    competitorNote: 'Quote-based pricing from $100+/mo. US-focused with limited international features. Basic feature set with no F&B, no multi-property, no advanced analytics.',
  },
  advantages: [
    'Four times the modules (36 vs 8+) at roughly half the starting price',
    'Full multi-property chain management — innRoad supports single properties only',
    'Complete F&B module with POS, room service, kitchen display, and menu management',
    'Advanced revenue management with dynamic pricing, forecasting, and simulators',
    'Global reach across 40+ countries vs innRoad US-only focus',
    'Multi-currency billing and regional tax compliance for international properties',
    'Loyalty program, guest CRM, and automated communications included',
    'Free onboarding and data migration with 24/7 dedicated support',
  ],
  verdict: {
    title: 'Our Verdict',
    summary: 'innRoad is a straightforward cloud PMS for US-based independent hotels that need basic reservation and front desk management. HotelStack.io offers a vastly more comprehensive platform with four times the modules, multi-property support, F&B/POS, advanced revenue tools, and global reach — all at a lower price point. For any hotel planning to grow beyond basic PMS needs, HotelStack.io is the clear choice.',
    bestFor: [
      'Independent hotels wanting to upgrade from basic PMS to a complete platform',
      'Properties needing F&B, POS, and operational tools that innRoad lacks',
      'Hotels expanding internationally needing multi-currency and compliance tools',
      'Properties wanting multi-property management for future growth',
      'Hotels seeking advanced revenue management beyond basic rate setting',
    ],
  },
  faqItems: [
    { question: 'Can I migrate from innRoad to HotelStack.io?', answer: 'Yes. Our migration team handles complete data transfer from innRoad including guest profiles, reservation history, rate plans, and room configurations. Migration is free and typically completed within 48 hours with zero downtime.' },
    { question: 'Does HotelStack.io support independent US hotels like innRoad?', answer: 'Absolutely. HotelStack.io supports independent hotels in the US and 40+ other countries. US hotels get all 36 modules including features innRoad lacks like F&B/POS, revenue management, and multi-property support — starting at $49/month.' },
    { question: 'How does HotelStack.io compare to innRoad for GDS connectivity?', answer: 'Both platforms offer GDS connectivity via Amadeus and Sabre. HotelStack.io additionally connects to 100+ OTAs globally, offers metasearch integration, and includes a commission-free booking engine (BookMyRoom.io) — all included in the base price.' },
    { question: 'Can HotelStack.io handle multiple properties unlike innRoad?', answer: 'Yes. HotelStack.io includes full chain management with a centralized dashboard, cross-property analytics, shared rate distribution, brand standards enforcement, and consolidated reporting. innRoad is designed for single-property operations only.' },
  ],
};

/* ========================================================================== */
/*  HotelStack.io vs WebRezPro                                                */
/* ========================================================================== */

export const HOTELSTACK_VS_WEBREZPRO: ComparisonConfig = {
  slug: 'hotelstack-vs-webrezpro',
  competitorName: 'WebRezPro',
  heroTitle: 'HotelStack.io vs WebRezPro',
  heroSubtitle: 'WebRezPro is a Canadian cloud PMS with an accounting focus targeting small-to-mid hotels. HotelStack.io delivers a modern, feature-rich platform with 36 modules, enterprise-grade UI, advanced revenue tools, and full F&B/POS — designed for the next generation of hospitality.',
  overviewComparison: [
    { label: 'Total Modules', hotelstack: '36 Integrated Modules', competitor: '10+ Core Features' },
    { label: 'Pricing', hotelstack: 'From $49/mo', competitor: 'From $75+/mo' },
    { label: 'UI/UX Quality', hotelstack: 'Modern Dark Luxury', competitor: 'Functional / Dated' },
    { label: 'F&B / POS', hotelstack: 'Built-In', competitor: 'Not Available' },
    { label: 'Revenue Management', hotelstack: 'Advanced (Built-In)', competitor: 'Basic Rate Tools' },
    { label: 'Multi-Property', hotelstack: 'Full Chain Management', competitor: 'Limited' },
  ],
  featureTable: [
    {
      category: 'Core PMS',
      features: [
        { name: 'Cloud-Based PMS', hotelstack: true, competitor: true },
        { name: 'Visual Room Board', hotelstack: true, competitor: true },
        { name: 'Reservation Management', hotelstack: true, competitor: true },
        { name: 'Night Audit Wizard', hotelstack: true, competitor: true },
        { name: 'Guest Folio Management', hotelstack: true, competitor: true },
        { name: 'Online Check-In Portal', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Accounting & Financials',
      features: [
        { name: 'Integrated Accounting', hotelstack: true, competitor: true },
        { name: 'P&L Statement', hotelstack: true, competitor: true },
        { name: 'Accounts Receivable', hotelstack: true, competitor: true },
        { name: 'Multi-Currency Billing', hotelstack: true, competitor: 'Limited' },
        { name: 'Regional Tax Compliance', hotelstack: true, competitor: 'CAD/USD Focus' },
        { name: 'Custom Invoice Templates', hotelstack: true, competitor: true },
      ],
    },
    {
      category: 'Revenue & Distribution',
      features: [
        { name: 'Channel Manager (100+ OTAs)', hotelstack: true, competitor: 'Fewer OTAs' },
        { name: 'Dynamic Pricing Rules', hotelstack: true, competitor: 'Basic' },
        { name: 'What-If Revenue Simulator', hotelstack: true, competitor: false },
        { name: 'Competitor Rate Monitor', hotelstack: true, competitor: false },
        { name: 'Demand Forecasting', hotelstack: true, competitor: false },
        { name: 'Commission-Free Booking Engine', hotelstack: true, competitor: true },
      ],
    },
    {
      category: 'F&B & Operations',
      features: [
        { name: 'POS Terminal', hotelstack: true, competitor: false },
        { name: 'Room Service Module', hotelstack: true, competitor: false },
        { name: 'Kitchen Display System', hotelstack: true, competitor: false },
        { name: 'Housekeeping Management', hotelstack: true, competitor: true },
        { name: 'Maintenance Work Orders', hotelstack: true, competitor: false },
        { name: 'Staff Scheduling', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Guest & Enterprise',
      features: [
        { name: '360-Degree Guest Profiles', hotelstack: true, competitor: 'Basic' },
        { name: 'Loyalty Program Management', hotelstack: true, competitor: false },
        { name: 'Guest Communication Automation', hotelstack: true, competitor: 'Limited' },
        { name: 'Chain Dashboard', hotelstack: true, competitor: 'Limited' },
        { name: 'Cross-Property Analytics', hotelstack: true, competitor: false },
        { name: 'Brand Standards Enforcement', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Resilience & Offline',
      features: [
        { name: 'Offline-Capable PWA', hotelstack: true, competitor: false },
        { name: 'Offline Mutation Queue', hotelstack: true, competitor: false },
        { name: 'Background Sync on Reconnect', hotelstack: true, competitor: false },
        { name: 'Conflict-Aware Reconciliation', hotelstack: true, competitor: false },
        { name: 'Installable Front Desk', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Corporate & Group Sales',
      features: [
        { name: 'Corporate Rate Management', hotelstack: true, competitor: false },
        { name: 'Group Booking with Rooming Lists', hotelstack: true, competitor: false },
        { name: 'AR Aging & Production Reports', hotelstack: true, competitor: false },
        { name: 'Dining Reservations', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Staff & HR',
      features: [
        { name: 'Staff Directory & Scheduling', hotelstack: true, competitor: false },
        { name: 'Payroll Processing', hotelstack: true, competitor: false },
        { name: 'Leave & Attendance', hotelstack: true, competitor: false },
        { name: 'Performance Analytics', hotelstack: true, competitor: false },
      ],
    },
  ],
  pricingComparison: {
    hotelstackStarting: '$49',
    competitorStarting: '$75+',
    hotelstackNote: 'All 36 modules included. Modern enterprise-grade UI. Regional pricing in EUR, GBP, AED, INR, THB. Free onboarding and migration.',
    competitorNote: 'Per-room pricing from $75+/mo CAD. Functional but dated interface. Limited F&B, limited multi-property, primarily North American support.',
  },
  advantages: [
    'Over three times the modules (36 vs 10+) with a modern enterprise-grade interface',
    'Complete F&B module with POS, room service, and kitchen management included',
    'Advanced revenue management with dynamic pricing, forecasting, and what-if simulators',
    'Modern dark luxury UI designed for 2026 vs WebRezPro functional but dated interface',
    'Full multi-property chain management with centralized analytics and rate distribution',
    'Global reach across 40+ countries vs WebRezPro North American focus',
    'Loyalty program, guest CRM, and automated communications for repeat bookings',
    'Free onboarding and data migration with 24/7 dedicated support',
  ],
  verdict: {
    title: 'Our Verdict',
    summary: 'WebRezPro is a reliable Canadian PMS with strong accounting features for small-to-mid North American hotels. However, its dated UI, limited feature set, and lack of F&B, advanced revenue tools, and enterprise scalability make it a less competitive choice in 2026. HotelStack.io delivers a modern, comprehensive platform that matches WebRezPro on accounting while adding 26+ additional modules, a world-class interface, and global capabilities.',
    bestFor: [
      'Hotels wanting a modern UI upgrade without sacrificing accounting depth',
      'Properties needing F&B, POS, and kitchen management beyond basic PMS',
      'Hotels seeking advanced revenue management and dynamic pricing tools',
      'Properties expanding internationally beyond North America',
      'Growing hotel groups needing full multi-property chain management',
    ],
  },
  faqItems: [
    { question: 'Can I migrate from WebRezPro to HotelStack.io?', answer: 'Yes. Our migration team handles complete data transfer from WebRezPro including guest profiles, reservation history, rate plans, accounting data, and room configurations. Migration is free and typically completed within 48 hours with zero downtime.' },
    { question: 'Does HotelStack.io match WebRezPro accounting capabilities?', answer: 'Yes. HotelStack.io includes integrated accounting, P&L statements, accounts receivable, custom invoicing, and multi-currency billing. Plus, it adds 26+ additional modules including F&B, revenue management, guest CRM, corporate sales, and HR/payroll that WebRezPro lacks.' },
    { question: 'How does HotelStack.io UI compare to WebRezPro?', answer: 'HotelStack.io features a modern dark luxury interface built with the latest web technologies, offering responsive design across all devices. WebRezPro has a functional but dated interface that was designed for an earlier era of web applications.' },
    { question: 'Is HotelStack.io suitable for Canadian properties currently using WebRezPro?', answer: 'Absolutely. HotelStack.io supports multi-currency billing including CAD, Canadian tax compliance (GST/HST/PST), and connects to all major North American OTAs. Many Canadian properties switch for the additional modules and modern experience.' },
  ],
};
